/* Site Tabs — switches between panels that are all rendered server side.
 *
 * The first tab and panel ship with `active`; this only moves that class (and
 * the ARIA state) to the tab that gets picked. Tabs and panels are found by
 * their roles and paired by order, so the markup around them can change
 * without touching this. Arrow keys, Home and End move between tabs as the
 * ARIA tabs pattern expects.
 */
class SiteTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => this.select(index));
      tab.addEventListener("keydown", (event) => this.onKeydown(event, index));
    });

    // Theme editor: picking a tab block in the sidebar opens its panel.
    this.addEventListener("shopify:block:select", (event) => {
      const index = this.tabs.indexOf(event.target);
      if (index > -1) this.select(index);
    });
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    const next = {
      ArrowRight: index === last ? 0 : index + 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }[event.key];
    if (next === undefined) return;

    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }

  select(index) {
    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle("active", isActive);
      tab.setAttribute("aria-selected", isActive);
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel, i) => panel.classList.toggle("active", i === index));
  }
}

if (!customElements.get("site-tabs")) customElements.define("site-tabs", SiteTabs);
